import { useCallback, useMemo } from "react";

import queryString from "query-string";
import { useLocation } from "react-router-dom";

import { webAppURL } from "../config";
import {
  SearchParam,
  SubscriptionPeriod,
  type SubscriptionTier,
  type SupportedLocale,
} from "../types";
import { PageSource } from "../types/tracking";
import { stringify } from "../urlUtils";

const useUrlHelper = () => {
  const location = useLocation();

  const isFormEmbedded = useCallback(() => {
    if (location.search === "") {
      return false;
    }

    const query = queryString.parse(location.search);
    const source: PageSource | "embedded" | null =
      (query.source as PageSource | "embedded") || null;

    switch (source) {
      case "embedded":
      case PageSource.Download:
      case PageSource.Pricing:
        return true;

      default:
        return false;
    }
  }, [location]);

  const getLocale = useCallback((): SupportedLocale => {
    const query = queryString.parse(location.search);
    switch (query.lang) {
      case "de":
        return "de";
      case "es":
        return "es";
      case "fr":
        return "fr";
      case "it":
        return "it";
      case "ja":
        return "ja";
      case "ko":
        return "ko";
      case "pt-BR":
      case "pt_BR":
      case "pt":
        return "pt-BR";
      case "ru":
        return "ru";
      case "zh":
      case "zh-hans":
      case "zh-Hans":
        return "zh-hans";
      case "zh-hant":
      case "zh-Hant":
        return "zh-hant";
      default:
        return "en";
    }
  }, [location]);

  const getQueryParams = useCallback(() => {
    return queryString.parse(location.search);
  }, [location]);

  const getBuyNowButtonURL = useCallback(
    (
      postMessageQuery: string,
      tier: SubscriptionTier,
      period?: SubscriptionPeriod,
      coupon?: string
    ): string => {
      const queryParameters = queryString.parse(postMessageQuery);
      const periodToPurchase = period || SubscriptionPeriod.Yearly;

      const buttonQueryParameters = {
        ...queryParameters,
        period: periodToPurchase,
        tier,
        source: PageSource.Pricing,
        ...(coupon && { coupon }),
      };

      return `${webAppURL}/checkout?${queryString.stringify(buttonQueryParameters)}`;
    },
    []
  );

  const isInApp = useMemo(() => {
    const queryParams = queryString.parse(location.search);
    return queryParams[SearchParam.InApp] === "true";
  }, [location]);

  const isFromApp = useMemo(() => {
    const queryParams = queryString.parse(location.search);
    return queryParams[SearchParam.Layout] === "fromapp";
  }, [location]);

  const urlHelperObj = useMemo(
    () => ({
      isFormEmbedded,
      getLocale,
      getQueryParams,
      stringify,
      getBuyNowButtonURL,
      isInApp,
      isFromApp,
    }),
    [isFormEmbedded, getLocale, getQueryParams, getBuyNowButtonURL, isInApp, isFromApp]
  );

  return urlHelperObj;
};

export default useUrlHelper;
